import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { analysisAPI } from '../services/api';
import { formatCurrency as fmt } from '../utils/format';

function Tile({ label, value, sub, hint, color = '#111827' }) {
  return (
    <div className="stat-tile" style={{ borderLeft: `4px solid ${color}` }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color }}>{value}</div>
      {sub ? <div className="stat-sub">{sub}</div> : null}
      {hint ? <div className="stat-sub" style={{ fontSize: 11, marginTop: 4 }}>{hint}</div> : null}
    </div>
  );
}

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '—';
  }
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return '—';
  }
}

function recoveryColor(pct) {
  if (pct >= 90) return '#15803d';
  if (pct >= 70) return '#b45309';
  return '#b91c1c';
}

function dueChipColor(daysToDue) {
  if (daysToDue < 0) return { bg: '#fee2e2', fg: '#991b1b' };
  if (daysToDue <= 3) return { bg: '#fef3c7', fg: '#92400e' };
  if (daysToDue <= 7) return { bg: '#e0f2fe', fg: '#075985' };
  return { bg: '#f1f5f9', fg: '#334155' };
}

function dueLabel(daysToDue) {
  if (daysToDue < 0) return `${Math.abs(daysToDue)}d overdue`;
  if (daysToDue === 0) return 'Today';
  if (daysToDue === 1) return 'Tomorrow';
  return `In ${daysToDue}d`;
}

function toNum(v) {
  const n = Number(v || 0);
  return Number.isFinite(n) ? n : 0;
}

export default function BusinessAnalysis() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analysis, setAnalysis] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await analysisAPI.get({ period: 'month', days: 7 });
      setAnalysis(res.data?.analysis || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Analysis load failed. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const trendMax = useMemo(() => {
    const list = analysis?.trend || [];
    return Math.max(...list.map((t) => Number(t.sales || 0)), 1);
  }, [analysis]);

  const stock = analysis?.stock || {};
  const sales = analysis?.sales || {};
  const cash = analysis?.cash || {};
  const handover = analysis?.handover || {};
  const emi = analysis?.emi || {};
  const commission = analysis?.commission || {};
  const reports = analysis?.reports || {};

  const monthlyPerformance = emi.monthlyPerformance || [];
  const upcoming = emi.upcoming || {
    next7Days: 0,
    next15Days: 0,
    next30Days: 0,
    list: [],
    overdueList: [],
    totalUpcomingCount: 0,
    totalOverdueCount: 0
  };

  const monthlyMaxDemand = useMemo(() => {
    return Math.max(1, ...monthlyPerformance.map((row) => Number(row.demand || 0)));
  }, [monthlyPerformance]);

  const totalDemandHistory = monthlyPerformance.reduce((s, r) => s + toNum(r.demand), 0);
  const totalCollectedHistory = monthlyPerformance.reduce((s, r) => s + toNum(r.collected), 0);
  const totalPendingHistory = monthlyPerformance.reduce((s, r) => s + toNum(r.pending), 0);
  const overallRecoveryPct = totalDemandHistory > 0
    ? Math.round((totalCollectedHistory / totalDemandHistory) * 100)
    : 0;

  const timelineItems = useMemo(() => {
    const rows = [];
    (handover.recent || []).slice(0, 4).forEach((h) => {
      const d = h.handoverDate || h.createdAt;
      rows.push({
        key: `h-${h._id || d}`,
        when: d,
        label: 'Cash handover',
        detail: h.status ? String(h.status) : 'Sent',
        amount: toNum(h.amount)
      });
    });
    (reports.recent || []).slice(0, 4).forEach((r) => {
      const d = r.reportDate || r.createdAt;
      rows.push({
        key: `r-${r._id || d}`,
        when: d,
        label: 'Daily report',
        detail: r.status ? String(r.status) : '—',
        amount: null
      });
    });
    rows.sort((a, b) => new Date(b.when) - new Date(a.when));
    return rows.slice(0, 6);
  }, [handover.recent, reports.recent]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0 fw-700">Business Analysis</h4>
        <button className="btn btn-sm btn-outline-secondary" onClick={load} disabled={loading}>
          {loading ? 'Refreshing…' : '↻ Refresh'}
        </button>
      </div>

      {error ? <div className="alert-soft danger mb-3">{error}</div> : null}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" />
          <div className="text-muted mt-2">Loading full business analysis…</div>
        </div>
      ) : (
        <>
          <div className="row g-3 mb-4">
            <div className="col-6 col-xl-3">
              <Tile
                label="Available stock value"
                value={fmt(stock.stockSellValue)}
                sub={`${stock.units || 0} units · ${stock.totalProducts || 0} SKUs`}
                color="#0d6efd"
              />
            </div>
            <div className="col-6 col-xl-3">
              <Tile
                label="This month sales"
                value={fmt(sales.thisMonthSalesAmount)}
                sub={`${sales.thisMonthSalesCount || 0} entries`}
                hint={
                  sales.thisMonthEmiBooked
                    ? `Incl. EMI booked ${fmt(sales.thisMonthEmiBooked)}`
                    : null
                }
                color="#198754"
              />
            </div>
            <div className="col-6 col-xl-3">
              <Tile
                label="Cash in hand today"
                value={fmt(cash.cashInHandToday)}
                sub={`Cash ${fmt(cash.todayCashSales)} · EMI collect ${fmt(cash.emiCollectedToday)}`}
                color="#7c3aed"
              />
            </div>
            <div className="col-6 col-xl-3">
              <Tile
                label="Owner settlement"
                value={fmt(handover.thisMonthSent)}
                sub={`Pending confirmation ${fmt(handover.pendingConfirmation)}`}
                color={Number(handover.pendingConfirmation) > 0 ? '#b45309' : '#0ea5e9'}
              />
            </div>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-12">
              <div className="section-head blue d-flex flex-wrap justify-content-between align-items-center gap-2">
                <span>Upcoming EMI months — demand vs collection</span>
                <span className="small fw-normal" style={{ opacity: 0.85 }}>
                  Overall recovery:{' '}
                  <strong style={{ color: '#fde68a' }}>{overallRecoveryPct}%</strong> ·
                  Pending: <strong>{fmt(totalPendingHistory)}</strong>
                </span>
              </div>
              <div className="section-body">
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-2 monthly-perf-table">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th className="text-end">EMI demand</th>
                        <th className="text-end">Collected</th>
                        <th className="text-end">Pending</th>
                        <th>Recovery</th>
                        <th className="text-end">Overdue installments</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyPerformance.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-muted text-center py-3">
                            No EMI history available yet.
                          </td>
                        </tr>
                      ) : (
                        monthlyPerformance.map((row) => {
                          const fillPct = row.demand > 0
                            ? Math.min(100, Math.round((row.collected / row.demand) * 100))
                            : 0;
                          const widthPct = Math.min(100, Math.round((row.demand / monthlyMaxDemand) * 100));
                          return (
                            <tr key={row.month || row.monthLabel}>
                              <td><strong>{row.monthLabel}</strong></td>
                              <td className="text-end">{fmt(row.demand)}</td>
                              <td className="text-end" style={{ color: '#15803d' }}>
                                {fmt(row.collected)}
                              </td>
                              <td className="text-end" style={{ color: row.pending > 0 ? '#b91c1c' : '#475569' }}>
                                {fmt(row.pending)}
                              </td>
                              <td style={{ minWidth: 160 }}>
                                <div className="recovery-bar">
                                  <div
                                    className="recovery-bar-fill"
                                    style={{
                                      width: `${widthPct}%`,
                                      background: '#e2e8f0'
                                    }}
                                  />
                                  <div
                                    className="recovery-bar-fill recovery-bar-progress"
                                    style={{
                                      width: `${(widthPct * fillPct) / 100}%`,
                                      background: recoveryColor(row.recoveryPct)
                                    }}
                                  />
                                  <span style={{ color: recoveryColor(row.recoveryPct) }}>
                                    {row.recoveryPct}%
                                  </span>
                                </div>
                              </td>
                              <td className="text-end" style={{ color: row.overdueInstallments > 0 ? '#b91c1c' : '#475569' }}>
                                {row.overdueInstallments}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="small text-muted">
                  Shows next 6 months from today. Demand = EMI kist due that month (₹1500 × installments). Collected = kist payments received (down payment excluded).
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-12 col-xl-4">
              <Tile
                label="Upcoming · 7 days"
                value={fmt(upcoming.next7Days)}
                sub={`${upcoming.totalUpcomingCount} customers due in next 30 days`}
                color="#0d6efd"
              />
            </div>
            <div className="col-12 col-xl-4">
              <Tile
                label="Upcoming · 15 days"
                value={fmt(upcoming.next15Days)}
                sub="Expected EMI collection window"
                color="#7c3aed"
              />
            </div>
            <div className="col-12 col-xl-4">
              <Tile
                label="Upcoming · 30 days"
                value={fmt(upcoming.next30Days)}
                sub={`Already overdue: ${upcoming.totalOverdueCount}`}
                color={upcoming.totalOverdueCount > 0 ? '#b45309' : '#15803d'}
              />
            </div>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-lg-6">
              <div className="section-head green">Upcoming due customers</div>
              <div className="section-body">
                {(upcoming.list || []).length === 0 ? (
                  <div className="text-muted small">No EMI installment due in the next 30 days.</div>
                ) : (
                  upcoming.list.map((row) => {
                    const chip = dueChipColor(row.daysToDue);
                    return (
                      <div className="upcoming-row" key={row.emiId || `${row.customerPhone}-${row.dueDate}`}>
                        <div className="upcoming-row-info">
                          <div className="upcoming-row-name">
                            {row.customerName}
                            {row.productName ? (
                              <span className="text-muted small ms-1">· {row.productName}</span>
                            ) : null}
                          </div>
                          <div className="small text-muted">
                            {row.customerPhone ? (
                              <a href={`tel:${row.customerPhone}`}>{row.customerPhone}</a>
                            ) : '—'}
                            {' · '}
                            EMI {row.installmentNo}/{row.totalInstallments}
                            {' · '}
                            Due {formatDate(row.dueDate)}
                          </div>
                        </div>
                        <div className="upcoming-row-meta">
                          <span
                            className="badge upcoming-due-badge"
                            style={{ background: chip.bg, color: chip.fg }}
                          >
                            {dueLabel(row.daysToDue)}
                          </span>
                          <strong className="ms-2">{fmt(row.monthlyAmount)}</strong>
                        </div>
                      </div>
                    );
                  })
                )}
                <div className="mt-2">
                  <Link to="/emi" className="btn btn-sm btn-outline-primary">
                    Open EMI Collections
                  </Link>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="section-head amber">Risk &amp; recovery</div>
              <div className="section-body">
                <div className="analysis-row">
                  <span>Active EMI accounts</span>
                  <strong>{emi.active || 0}</strong>
                </div>
                <div className="analysis-row">
                  <span>Currently overdue customers</span>
                  <strong style={{ color: emi.overdueCount > 0 ? '#b91c1c' : '#198754' }}>
                    {emi.overdueCount || 0}
                  </strong>
                </div>
                <div className="analysis-row">
                  <span>NPA count</span>
                  <strong>{emi.npaCount || 0}</strong>
                </div>
                <div className="analysis-row">
                  <span>NPA exposure</span>
                  <strong>{fmt(emi.npaAmount)}</strong>
                </div>
                <div className="analysis-row">
                  <span>Monthly EMI demand</span>
                  <strong>{fmt(emi.monthlyDemand)}</strong>
                </div>
                <div className="analysis-row">
                  <span>Recovery rate (life-to-date)</span>
                  <strong>{emi.recoveryRatePct || 0}%</strong>
                </div>
                {(upcoming.overdueList || []).length > 0 ? (
                  <>
                    <div className="text-muted small mt-3 mb-1">Top overdue installments</div>
                    {upcoming.overdueList.slice(0, 5).map((row) => (
                      <div className="upcoming-row" key={`od-${row.emiId}`}>
                        <div className="upcoming-row-info">
                          <div className="upcoming-row-name">{row.customerName}</div>
                          <div className="small text-muted">
                            {row.customerPhone ? (
                              <a href={`tel:${row.customerPhone}`}>{row.customerPhone}</a>
                            ) : '—'}
                            {' · Due '}
                            {formatDate(row.dueDate)}
                          </div>
                        </div>
                        <div className="upcoming-row-meta">
                          <span
                            className="badge upcoming-due-badge"
                            style={{ background: '#fee2e2', color: '#991b1b' }}
                          >
                            {dueLabel(row.daysToDue)}
                          </span>
                          <strong className="ms-2">{fmt(row.monthlyAmount)}</strong>
                        </div>
                      </div>
                    ))}
                  </>
                ) : null}
              </div>
            </div>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-lg-6">
              <div className="section-head blue">Stock &amp; investment</div>
              <div className="section-body">
                <div className="analysis-row">
                  <span>Current stock sell value</span>
                  <strong>{fmt(stock.stockSellValue)}</strong>
                </div>
                <div className="analysis-row">
                  <span>Total stock units</span>
                  <strong>{stock.units || 0}</strong>
                </div>
                <div className="analysis-row">
                  <span>Total SKUs</span>
                  <strong>{stock.totalProducts || 0}</strong>
                </div>
                <div className="analysis-row">
                  <span>Low stock SKUs</span>
                  <strong>{stock.lowStockCount || 0}</strong>
                </div>
                <div className="analysis-row">
                  <span>Out of stock SKUs</span>
                  <strong>{stock.outOfStockCount || 0}</strong>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="section-head">Cash &amp; collection</div>
              <div className="section-body">
                <div className="analysis-row">
                  <span>Cash sales today</span>
                  <strong>{fmt(cash.todayCashSales)}</strong>
                </div>
                <div className="analysis-row">
                  <span>UPI sales today</span>
                  <strong>{fmt(cash.todayUpiSales)}</strong>
                </div>
                <div className="analysis-row">
                  <span>EMI down payment today</span>
                  <strong>{fmt(cash.todayEmiDownPayment)}</strong>
                </div>
                <div className="analysis-row">
                  <span>EMI collected today</span>
                  <strong>{fmt(cash.emiCollectedToday)}</strong>
                </div>
                <div className="analysis-row">
                  <span>EMI collected this month</span>
                  <strong>{fmt(cash.emiCollectedThisMonth)}</strong>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-lg-6">
              <div className="section-head green">Commission &amp; handover</div>
              <div className="section-body">
                <div className="analysis-row">
                  <span>Commission confirmed (month)</span>
                  <strong>{fmt(commission.confirmedThisMonth)}</strong>
                </div>
                <div className="analysis-row">
                  <span>Commission pending payout</span>
                  <strong>{fmt(commission.pendingPayout)}</strong>
                </div>
                <div className="analysis-row">
                  <span>Commission paid (month)</span>
                  <strong>{fmt(commission.paidThisMonth)}</strong>
                </div>
                <div className="analysis-row">
                  <span>Closing commission balance</span>
                  <strong>{fmt(commission.closingBalance)}</strong>
                </div>
                <div className="analysis-row">
                  <span>Total handover sent</span>
                  <strong>{fmt(handover.totalSent)}</strong>
                </div>
                <div className="analysis-row">
                  <span>Pending owner confirmation</span>
                  <strong>{fmt(handover.pendingConfirmation)}</strong>
                </div>
                <div className="mt-2 d-flex flex-wrap gap-2">
                  <Link to="/commission" className="btn btn-sm btn-outline-success">
                    My Commission
                  </Link>
                  <Link to="/cash-handover" className="btn btn-sm btn-outline-secondary">
                    Cash Handover
                  </Link>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="section-head">Recent activity</div>
              <div className="section-body analysis-timeline">
                {timelineItems.length === 0 ? (
                  <div className="text-muted small">No recent handovers or reports in snapshot.</div>
                ) : (
                  timelineItems.map((row) => (
                    <div className="analysis-timeline-row" key={row.key}>
                      <div>
                        <div>{row.label}</div>
                        <small>{row.detail}</small>
                      </div>
                      <div className="text-end">
                        {row.amount != null ? <strong>{fmt(row.amount)}</strong> : null}
                        <div>
                          <small>{formatWhen(row.when)}</small>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-lg-6">
              <div className="section-head">Top overdue customers</div>
              <div className="section-body">
                {(emi.topOverdue || []).slice(0, 5).map((item) => (
                  <div className="analysis-row" key={item.emiId}>
                    <span>
                      {item.customerName} ·{' '}
                      <a href={`tel:${item.customerPhone}`}>{item.customerPhone}</a>
                    </span>
                    <strong>
                      {fmt(item.outstandingAmount)} ({item.daysOverdue}d)
                    </strong>
                  </div>
                ))}
                {(!emi.topOverdue || emi.topOverdue.length === 0) ? (
                  <div className="text-muted small">No overdue customer in top list.</div>
                ) : null}
              </div>
            </div>
            <div className="col-lg-6">
              <div className="section-head">7 day sales trend</div>
              <div className="section-body">
                <div className="d-flex align-items-end gap-2" style={{ minHeight: 110 }}>
                  {(analysis?.trend || []).slice(-7).map((t) => {
                    const h = Math.max(4, Math.round((Number(t.sales || 0) / trendMax) * 90));
                    return (
                      <div key={t.date} className="text-center" style={{ width: '14%' }}>
                        <div
                          style={{
                            height: h,
                            background: 'linear-gradient(180deg, #6366f1, #0ea5e9)',
                            borderRadius: 4
                          }}
                        />
                        <div className="small text-muted mt-1">
                          {new Date(t.date).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short'
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="small text-muted mt-2">
                  Bar height follows recorded sales value per day (branch scope).
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
